import React, { useState, useEffect } from 'react';
import API from '../../api/axios';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiXCircle } from 'react-icons/fi';
import { formatCreatedAt } from '../../utils/dateUtils';

const AdminInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [patients, setPatients] = useState([]);
  const [form, setForm] = useState({
    patient_id: '', motif: '', description: '', amount: '',
    currency: 'CDF', due_date: '', hospital_name: 'Nomargueri', department: '', notes: '',
  });

  const fetchInvoices = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const res = await API.get('/admin/invoices', { params });
      setInvoices(res.data.data.invoices);
      setPagination({ page: res.data.data.pagination.page, totalPages: res.data.data.pagination.totalPages });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const res = await API.get('/admin/patients');
      setPatients(res.data.data.patients);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchPatients();
  }, [statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInvoices(1);
  };

  const resetForm = () => {
    setForm({
      patient_id: '', motif: '', description: '', amount: '',
      currency: 'CDF', due_date: '', hospital_name: 'Nomargueri', department: '', notes: '',
    });
    setEditingInvoice(null);
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (inv) => {
    setEditingInvoice(inv);
    setForm({
      patient_id: inv.patient_id,
      motif: inv.motif,
      description: inv.description || '',
      amount: inv.amount,
      currency: inv.currency,
      due_date: inv.due_date || '',
      hospital_name: inv.hospital_name || '',
      department: inv.department || '',
      notes: inv.notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingInvoice) {
        await API.put(`/invoices/${editingInvoice.id}`, form);
        toast.success('Facture mise à jour.');
      } else {
        await API.post('/invoices', form);
        toast.success('Facture créée avec succès.');
      }
      setShowModal(false);
      resetForm();
      fetchInvoices(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur.');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Annuler cette facture ?')) return;
    try {
      await API.delete(`/invoices/${id}`);
      toast.success('Facture annulée.');
      fetchInvoices(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur.');
    }
  };

  const getStatusBadge = (status) => {
    const map = { pending: 'En attente', partial: 'Partiel', paid: 'Payée', overdue: 'En retard', cancelled: 'Annulée' };
    return <span className={`badge badge-${status}`}>{map[status] || status}</span>;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 600 }}>Gestion des factures</h2>
          <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>Créez et gérez les factures des patients</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus /> Nouvelle facture</button>
      </div>

      {/* Filtres */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, flex: 1 }}>
            <input className="form-input" placeholder="Rechercher par N° ou motif..." value={search}
              onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 300 }} />
            <button type="submit" className="btn btn-outline">Rechercher</button>
          </form>
          <select className="form-select" style={{ width: 160 }} value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Tous statuts</option>
            <option value="pending">En attente</option>
            <option value="partial">Partiel</option>
            <option value="paid">Payée</option>
            <option value="overdue">En retard</option>
            <option value="cancelled">Annulée</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="loading-page"><div className="spinner"></div></div>
        ) : invoices.length === 0 ? (
          <div className="empty-state"><h3>Aucune facture</h3></div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>N° Facture</th>
                    <th>Patient</th>
                    <th>Motif</th>
                    <th>Montant</th>
                    <th>Payé</th>
                    <th>Statut</th>
                    <th>Créée par</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id}>
                      <td style={{ fontWeight: 600, fontSize: 13 }}>{inv.invoice_number}</td>
                      <td>
                        {inv.patient?.first_name} {inv.patient?.last_name}
                        <br /><span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{inv.patient?.phone}</span>
                      </td>
                      <td>{inv.motif}</td>
                      <td style={{ fontWeight: 600 }}>{parseFloat(inv.amount).toLocaleString()} {inv.currency}</td>
                      <td>{parseFloat(inv.amount_paid).toLocaleString()} {inv.currency}</td>
                      <td>{getStatusBadge(inv.status)}</td>
                      <td>
                        {inv.creator?.first_name} {inv.creator?.last_name}
                        <br /><span className={`badge badge-${inv.creator?.role}`}>{inv.creator?.role}</span>
                      </td>
                      <td>{formatCreatedAt(inv)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                            <>
                              <button className="btn btn-outline btn-sm" onClick={() => openEdit(inv)} title="Modifier">
                                <FiEdit2 />
                              </button>
                              <button className="btn btn-danger btn-sm" onClick={() => handleCancel(inv.id)} title="Annuler">
                                <FiXCircle />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button disabled={pagination.page <= 1} onClick={() => fetchInvoices(pagination.page - 1)}>Précédent</button>
                <span style={{ fontSize: 14, color: 'var(--gray-600)' }}>Page {pagination.page} / {pagination.totalPages}</span>
                <button disabled={pagination.page >= pagination.totalPages} onClick={() => fetchInvoices(pagination.page + 1)}>Suivant</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal Création / Édition */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <h2>{editingInvoice ? 'Modifier la facture' : 'Nouvelle facture'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {!editingInvoice && (
                  <div className="form-group">
                    <label className="form-label">Patient *</label>
                    <select className="form-select" value={form.patient_id}
                      onChange={(e) => setForm({ ...form, patient_id: e.target.value })} required>
                      <option value="">Sélectionner un patient</option>
                      {patients.map((p) => (
                        <option key={p.id} value={p.id}>{p.first_name} {p.last_name} - {p.phone}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Motif de la facture *</label>
                  <input className="form-input" placeholder="Ex: Consultation, Hospitalisation, Chirurgie..."
                    value={form.motif} onChange={(e) => setForm({ ...form, motif: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-textarea" rows={3} placeholder="Description détaillée des soins..."
                    value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Montant *</label>
                    <input type="number" className="form-input" placeholder="0.00" step="0.01" min="0.01"
                      value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Devise</label>
                    <select className="form-select" value={form.currency}
                      onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                      <option value="CDF">CDF (Franc Congolais)</option>
                      <option value="USD">USD (Dollar)</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Date d'échéance</label>
                    <input type="date" className="form-input" value={form.due_date}
                      onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Département</label>
                    <input className="form-input" placeholder="Ex: Cardiologie, Pédiatrie..."
                      value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes internes</label>
                  <textarea className="form-textarea" rows={2} placeholder="Notes visibles uniquement par les admins..."
                    value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">
                  {editingInvoice ? 'Mettre à jour' : 'Créer la facture'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInvoices;
